import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/lib/seo';
import { usePageMedia } from '@/lib/usePageMedia';
import { useOpeningHours } from '@/hooks/useOpeningHours';

export default function Contact() {
  const { get } = usePageMedia();
  const heroImg = get('contact', 'hero');
  const { storefront, formatHours, isOpenNow } = useOpeningHours();
  const hoursLabel = storefront ? formatHours(storefront) : 'Mon–Sat 9am–6pm';
  const open = !!storefront && isOpenNow(storefront);
  useSeo({
    title: 'Contact Motion Reality — Sim Racing Rigs & Bookings in Bromborough',
    description: 'Contact Motion Reality for custom sim racing rigs, hardware and simulator experience bookings. Unit 9, Croft Business Park, Bromborough, Liverpool CH62 3RA. Call 0151 528 3001.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Motion Reality',
      url: 'https://motionreality.co.uk/contact',
      mainEntity: {
        '@type': 'Organization',
        name: 'Motion Reality',
        telephone: '+441515283001',
        email: 'sales@motionreality.co.uk',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Unit 9, Croft Business Park, Carrock Road',
          addressLocality: 'Bromborough',
          addressRegion: 'Merseyside',
          postalCode: 'CH62 3RA',
          addressCountry: 'GB',
        },
      },
    },
  });
  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="relative overflow-hidden py-20 px-6 lg:px-10 border-b border-white/5">
        {heroImg && (
          <>
            <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0f]/70 to-[#0d0d0f]" />
          </>
        )}
        <div className="relative max-w-5xl mx-auto">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">GET IN TOUCH</p>
          <h1 className="text-white font-black text-4xl md:text-6xl uppercase leading-tight">
            CONTACT US
          </h1>
        </div>
      </section>

      {/* Info cards */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {[
            { icon: Phone, label: 'CALL US', value: '0151 528 3001', href: 'tel:01515283001', sub: hoursLabel },
            { icon: Mail, label: 'EMAIL US', value: 'sales@motionreality.co.uk', href: 'mailto:sales@motionreality.co.uk', sub: 'We aim to reply within 2 hours' },
            { icon: MapPin, label: 'VISIT US', value: 'Unit 9, Croft Business Park, Bromborough, CH62 3RA', href: null, sub: 'By appointment', mapUrl: 'https://www.google.com/maps/search/?api=1&query=Unit+9+Croft+Business+Park+Carrock+Road+Bromborough+CH62+3RA' },
            { icon: Clock, label: 'OPENING HOURS', value: hoursLabel, href: null, sub: open ? 'Open now' : 'Closed now' },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#111115] border border-white/5 p-7 group hover:border-primary/20 transition-colors"
            >
              <div className="w-10 h-10 bg-primary/10 flex items-center justify-center mb-5">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-2">{item.label}</p>
              {item.href ? (
                <a href={item.href} className="text-white font-semibold text-sm hover:text-primary transition-colors block mb-1">{item.value}</a>
              ) : (
                <p className="text-white font-semibold text-sm mb-1">{item.value}</p>
              )}
              <p className="text-white/30 text-xs">{item.sub}</p>
              {item.mapUrl && (
                <a
                  href={item.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary text-xs font-bold tracking-wide mt-3 hover:text-primary/80 transition-colors"
                >
                  GET DIRECTIONS <ArrowRight className="w-3 h-3" />
                </a>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="max-w-5xl mx-auto bg-primary p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white/60 text-xs font-bold tracking-widest mb-2">READY TO RACE?</p>
            <h3 className="text-white font-black text-2xl md:text-3xl uppercase">BOOK YOUR SESSION</h3>
          </div>
          <Link to="/bookings" className="shrink-0">
            <button className="bg-white text-primary hover:bg-white/90 font-bold text-sm px-8 py-3.5 transition-colors flex items-center gap-2">
              BOOK NOW <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}